/**
 * Importar audio (SDD §12.2): arrastrar o elegir, validar tipo y tamaño en el
 * cliente (la API vuelve a validar y revisa los magic bytes), subir directo al
 * almacenamiento con URL prefirmada y completar.
 */
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { IMPORT_MIME_TYPES, LANGUAGE_LABELS, PLANS, formatMinutes } from '@eco/shared'
import { useApp, useToast, errorText } from '../components/AppContext'
import { uploadWithProgress } from '../lib/api'
import { useAsync, useDocumentTitle } from '../lib/hooks'

type Mime = (typeof IMPORT_MIME_TYPES)[number]

/** Tipo MIME aceptado para un archivo (el navegador a veces no lo informa). */
export function mimeOf(file: { name: string; type: string }): Mime | null {
  const t = file.type === 'audio/x-m4a' || file.type === 'audio/m4a' ? 'audio/mp4' : file.type
  if ((IMPORT_MIME_TYPES as readonly string[]).includes(t)) return t as Mime
  const ext = file.name.toLowerCase().split('.').pop()
  const byExt: Record<string, Mime> = {
    mp3: 'audio/mpeg',
    m4a: 'audio/mp4',
    mp4: 'video/mp4',
    wav: 'audio/wav',
    webm: 'audio/webm',
    ogg: 'audio/ogg',
    opus: 'audio/ogg'
  }
  return (ext && byExt[ext]) || null
}

export function Import() {
  useDocumentTitle('Importar audio')
  const { api, usage, refreshUsage } = useApp()
  const toast = useToast()
  const nav = useNavigate()
  const input = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [titulo, setTitulo] = useState('')
  const [projectId, setProjectId] = useState('')
  const [idioma, setIdioma] = useState<'es' | 'en' | 'pt'>('es')
  // Cuándo fue la reunión: por defecto la fecha del archivo (casi siempre, cuando se grabó)
  const [fecha, setFecha] = useState('')
  const [progress, setProgress] = useState<number | null>(null)
  const [over, setOver] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abort = useRef<AbortController | null>(null)
  const projects = useAsync(() => api.projects(), [api])
  const plan = PLANS[usage?.plan ?? 'free']

  function pick(f: File | undefined) {
    setError(null)
    if (!f) return
    if (!mimeOf(f))
      return setError('Ese formato no se puede importar. Usa MP3, M4A, WAV, WebM, OGG o MP4.')
    if (f.size > plan.topeImportMb * 1024 * 1024)
      return setError(
        `El archivo supera ${plan.topeImportMb >= 1024 ? `${plan.topeImportMb / 1024} GB` : `${plan.topeImportMb} MB`}, el máximo de tu plan.`
      )
    setFile(f)
    setTitulo(f.name.replace(/\.[a-z0-9]+$/i, '').slice(0, 200))
    setFecha(localDateTime(f.lastModified ? new Date(f.lastModified) : new Date()))
  }

  async function upload() {
    if (!file) return
    const tipo = mimeOf(file)!
    setProgress(0)
    setError(null)
    const ctl = new AbortController()
    abort.current = ctl
    try {
      const p = await api.presignImport({ nombre: file.name.slice(0, 200), bytes: file.size, tipo })
      if (ctl.signal.aborted) return
      await uploadWithProgress(p.url, file, tipo, setProgress, ctl.signal)
      if (ctl.signal.aborted) return
      const done = await api.completeImport({
        recordingId: p.recordingId,
        titulo: titulo.trim() || undefined,
        projectId: projectId || null,
        idioma,
        fechaInicio: fecha ? new Date(fecha).toISOString() : undefined
      })
      void refreshUsage()
      if (done.decision === 'demasiado_larga')
        toast('El audio supera la duración máxima de tu plan.', 'error')
      else if (done.decision === 'retenida')
        toast('Sin horas disponibles: quedó retenida hasta que mejores tu plan.')
      else toast('Audio recibido: estamos transcribiendo.')
      nav(`/r/${p.recordingId}`)
    } catch (e) {
      // Cancelar no es un error
      if (ctl.signal.aborted) return
      setError(errorText(e))
      setProgress(null)
    }
  }

  return (
    <div className="page narrow">
      <div className="page-head">
        <div className="grow">
          <h1>Importar audio</h1>
          <p className="muted">
            Sube la grabación de una reunión, entrevista o nota de voz. La transcribimos y
            escribimos las notas igual que con una grabación de eco.
          </p>
        </div>
      </div>
      {!file ? (
        <div
          className={`dropzone ${over ? 'over' : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => input.current?.click()}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && input.current?.click()}
          onDragOver={(e) => {
            e.preventDefault()
            setOver(true)
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault()
            setOver(false)
            pick(e.dataTransfer.files[0])
          }}
        >
          <h2>Arrastra un archivo aquí</h2>
          <p className="muted">
            o haz clic para elegirlo · MP3, M4A, WAV, WebM, OGG, MP4 · hasta{' '}
            {plan.topeImportMb >= 1024
              ? `${plan.topeImportMb / 1024} GB`
              : `${plan.topeImportMb} MB`}{' '}
            y {formatMinutes(plan.topeGrabacionMin)}
          </p>
          <input
            ref={input}
            type="file"
            hidden
            accept={[
              ...IMPORT_MIME_TYPES,
              '.mp3',
              '.m4a',
              '.wav',
              '.webm',
              '.ogg',
              '.opus',
              '.mp4'
            ].join(',')}
            onChange={(e) => pick(e.target.files?.[0])}
          />
        </div>
      ) : (
        <div className="card stack">
          <div className="row">
            <strong className="grow" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {file.name}
            </strong>
            <span className="faint mono">{(file.size / 1024 / 1024).toFixed(1)} MB</span>
          </div>
          <label className="field">
            <span>Título</span>
            <input
              value={titulo}
              maxLength={200}
              onChange={(e) => setTitulo(e.target.value)}
              disabled={progress !== null}
            />
          </label>
          <label className="field">
            <span>Fecha de la reunión</span>
            <input
              type="datetime-local"
              value={fecha}
              max={localDateTime(new Date())}
              onChange={(e) => setFecha(e.target.value)}
              disabled={progress !== null}
            />
          </label>
          <label className="field">
            <span>Proyecto</span>
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              disabled={progress !== null}
            >
              <option value="">Sin proyecto</option>
              {(projects.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Idioma en que se habla</span>
            <select
              value={idioma}
              onChange={(e) => setIdioma(e.target.value as 'es' | 'en' | 'pt')}
              disabled={progress !== null}
            >
              {(Object.keys(LANGUAGE_LABELS) as ('es' | 'en' | 'pt')[]).map((l) => (
                <option key={l} value={l}>
                  {LANGUAGE_LABELS[l]}
                </option>
              ))}
            </select>
          </label>
          {progress !== null && (
            <div className="stack" style={{ gap: 4 }} aria-live="polite">
              <div className="bar">
                <span style={{ width: `${Math.round(progress * 100)}%` }} />
              </div>
              <span className="faint" style={{ fontSize: 12 }}>
                Subiendo… {Math.round(progress * 100)}%
              </span>
            </div>
          )}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button
              className="btn ghost"
              onClick={() => {
                abort.current?.abort()
                setFile(null)
                setProgress(null)
              }}
            >
              {progress !== null ? 'Cancelar' : 'Elegir otro'}
            </button>
            <button
              className="btn primary"
              onClick={() => void upload()}
              disabled={progress !== null}
            >
              Subir y transcribir
            </button>
          </div>
        </div>
      )}
      {error && (
        <div className="notice error" role="alert" style={{ marginTop: 12 }}>
          {error}
        </div>
      )}
      <p className="faint" style={{ fontSize: 12, marginTop: 14 }}>
        El archivo se borra de nuestros servidores apenas termina la transcripción. Si el audio
        incluye a otras personas, asegúrate de haberles avisado que se grabó.
      </p>
    </div>
  )
}

/** Valor para <input type="datetime-local"> en la hora local (sin segundos). */
function localDateTime(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
}
